import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { userApi, ApiConfig } from "../services/api";
import { useToast } from "../contexts/ToastContext";
import {
    modelOptions,
    defaultPrimaryModel,
    defaultRefinementModel,
} from "../utils/modelOptions";
import { APP_CONFIG } from "config/app.config";

// Visibility constants for sections
const API_KEY_CONFIG_VISIBLE = false;
const DATA_SOURCE_CONFIG_VISIBLE = false;

interface DataSourceInfo {
    name: string;
    requestResultCount: number;
    includedResultCount: number;
    maximumResultCount: number;
    multiplierPerAdditional: number;
}

const DEFAULT_DATA_SOURCES: DataSourceInfo[] = [
    {
        name: "passio",
        requestResultCount: 50,
        includedResultCount: 50,
        maximumResultCount: 200,
        multiplierPerAdditional: 0.001,
    },
    {
        name: "edamam_ingredients",
        requestResultCount: 10,
        includedResultCount: 10,
        maximumResultCount: 30,
        multiplierPerAdditional: 0.05,
    },
    {
        name: "edamam_recipes",
        requestResultCount: 0,
        includedResultCount: 0,
        maximumResultCount: 10,
        multiplierPerAdditional: 0.05,
    },
    {
        name: "nutritionix",
        requestResultCount: 0,
        includedResultCount: 0,
        maximumResultCount: 20,
        multiplierPerAdditional: 0.1,
    },
    {
        name: "nutritionix_branded",
        requestResultCount: 0,
        includedResultCount: 0,
        maximumResultCount: 20,
        multiplierPerAdditional: 0.1,
    },
];

const ApiConfigTab: React.FC = () => {
    const { dbUser, refreshUser } = useAuth();
    const { showToast } = useToast();
    const [model, setModel] = useState(defaultPrimaryModel);
    const [refinementModel, setRefinementModel] = useState(
        defaultRefinementModel
    );
    const [dataSources, setDataSources] =
        useState<DataSourceInfo[]>(DEFAULT_DATA_SOURCES);
    const [hasChanges, setHasChanges] = useState(false);
    const [manualApiKey, setManualApiKey] = useState("");
    const [savingApiKey, setSavingApiKey] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [preset, setPreset] = useState<"faster" | "accurate">("faster");

    // Calculate total multiplier
    const calculateTotalMultiplier = () => {
        const additionalCosts = dataSources.reduce((total, source) => {
            if (source.requestResultCount > source.includedResultCount) {
                return (
                    total +
                    (source.requestResultCount - source.includedResultCount) *
                        source.multiplierPerAdditional
                );
            }
            return total;
        }, 0);
        return 1 + additionalCosts;
    };

    // Update preset based on current model selections
    const updatePresetFromModels = (primaryModel: string, refModel: string) => {
        if (primaryModel === APP_CONFIG.DEFAULT_MODEL && refModel === APP_CONFIG.DEFAULT_REFINEMENT_MODEL) {
            setPreset("faster");
        } else if (primaryModel === APP_CONFIG.ACCURATE_MODEL && refModel === APP_CONFIG.ACCURATE_REFINEMENT_MODEL) {
            setPreset("accurate");
        }
    };

    useEffect(() => {
        if (dbUser?.apiconfig) {
            const primaryModel = dbUser.apiconfig.model || APP_CONFIG.DEFAULT_MODEL;
            const refModel = dbUser.apiconfig.refinementmodel || APP_CONFIG.DEFAULT_REFINEMENT_MODEL;
            
            setModel(primaryModel);
            setRefinementModel(refModel);
            updatePresetFromModels(primaryModel, refModel);

            if (dbUser.apiconfig.datasourceconfig) {
                const savedConfig = dbUser.apiconfig.datasourceconfig;

                // Handle both old array format and new dictionary format
                if (Array.isArray(savedConfig)) {
                    // Legacy array format - merge with defaults
                    const updatedSources = DEFAULT_DATA_SOURCES.map(
                        (defaultSource) => {
                            const savedSource = savedConfig.find(
                                (s: any) => s.name === defaultSource.name
                            );
                            return savedSource
                                ? { ...defaultSource, ...savedSource }
                                : defaultSource;
                        }
                    );
                    setDataSources(updatedSources);
                } else if (typeof savedConfig === "object") {
                    // New dictionary format - convert back to array
                    const updatedSources = DEFAULT_DATA_SOURCES.map(
                        (defaultSource) => {
                            const savedSource = savedConfig[defaultSource.name];
                            return savedSource
                                ? { ...defaultSource, ...savedSource }
                                : defaultSource;
                        }
                    );
                    setDataSources(updatedSources);
                }
            }
        } else {
            // Default new users to "Faster" preset
            setPreset("faster");
            setModel(APP_CONFIG.DEFAULT_MODEL);
            setRefinementModel(APP_CONFIG.DEFAULT_REFINEMENT_MODEL);
        }
    }, [dbUser]);

    const handlePresetChange = (newPreset: "faster" | "accurate") => {
        setPreset(newPreset);
        setHasChanges(true);
        
        if (newPreset === "faster") {
            setModel(APP_CONFIG.DEFAULT_MODEL);
            setRefinementModel(APP_CONFIG.DEFAULT_REFINEMENT_MODEL);
        } else {
            setModel(APP_CONFIG.ACCURATE_MODEL);
            setRefinementModel(APP_CONFIG.ACCURATE_REFINEMENT_MODEL);
        }
    };

    const handleSave = async () => {
        if (!dbUser?.id) return;
        try {
            // Convert array of data sources to dictionary format expected by backend
            const datasourceconfig: Record<string, any> = {};
            dataSources.forEach((source) => {
                datasourceconfig[source.name] = {
                    requestResultCount: source.requestResultCount,
                    includedResultCount: source.includedResultCount,
                    maximumResultCount: source.maximumResultCount,
                    multiplierPerAdditional: source.multiplierPerAdditional,
                };
            });

            const config: ApiConfig = {
                model: model || undefined,
                refinementmodel: refinementModel || undefined,
                datasourceconfig: datasourceconfig,
            };
            await userApi.updateApiConfig(dbUser.id, config);
            setHasChanges(false);
            await refreshUser();
            showToast({
                message: "AI configuration saved successfully!",
                type: "success",
            });
        } catch (error) {
            console.error("Error saving config:", error);
            showToast({
                message: "Failed to save configuration",
                type: "error",
            });
        }
    };

    const formatDataSourceName = (name: string) => {
        return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const ToggleSwitch: React.FC<{
        checked: boolean;
        onChange: (checked: boolean) => void;
    }> = ({ checked, onChange }) => {
        return (
            <div
                onClick={() => onChange(!checked)}
                style={{
                    width: "48px",
                    height: "24px",
                    backgroundColor: checked ? "#3b82f6" : "#d1d5db",
                    borderRadius: "12px",
                    position: "relative",
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                }}
            >
                <div
                    style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        position: "absolute",
                        top: "2px",
                        left: checked ? "26px" : "2px",
                        transition: "left 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                />
            </div>
        );
    };

    const handleSaveApiKey = async () => {
        if (!dbUser?.id) return;
        try {
            setSavingApiKey(true);
            await userApi.updatePassioConfig(dbUser.id, {
                passioapikey: manualApiKey,
            });
            setManualApiKey("");
            await refreshUser();
            showToast({
                message: "Passio AI key saved successfully!",
                type: "success",
            });
        } catch (error) {
            console.error("Error saving Passio AI key:", error);
            showToast({
                message: "Failed to save Passio AI key",
                type: "error",
            });
        } finally {
            setSavingApiKey(false);
        }
    };

    return (
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
            <h2 style={{ fontSize: "24px", marginBottom: "20px" }}>
                AI Configuration
            </h2>

            <div
                style={{
                    backgroundColor: "white",
                    padding: "24px",
                    borderRadius: "12px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    marginBottom: "20px",
                    border: "1px solid #e5e7eb",
                }}
            >
                <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "flex-start",
                    marginBottom: "20px" 
                }}>
                    <div>
                        <h3 style={{ 
                            margin: "0 0 4px 0", 
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#111827"
                        }}>
                            AI Model Configuration
                        </h3>
                        <p style={{ 
                            margin: 0,
                            fontSize: "14px", 
                            color: "#6b7280" 
                        }}>
                            Choose your preferred balance between speed and accuracy
                        </p>
                    </div>
                </div>
                
                {/* Preset Selection Cards */}
                <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr", 
                    gap: "16px",
                    marginBottom: "24px"
                }}>
                    <div
                        onClick={() => handlePresetChange("faster")}
                        style={{
                            padding: "16px",
                            borderRadius: "8px",
                            border: preset === "faster" ? "2px solid #3b82f6" : "2px solid #e5e7eb",
                            backgroundColor: preset === "faster" ? "#eff6ff" : "#ffffff",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            marginBottom: "8px" 
                        }}>
                            <div style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                border: preset === "faster" ? "6px solid #3b82f6" : "2px solid #d1d5db",
                                marginRight: "12px",
                                transition: "all 0.2s ease",
                            }} />
                            <h4 style={{ 
                                margin: 0,
                                fontSize: "16px",
                                fontWeight: "600",
                                color: preset === "faster" ? "#1e40af" : "#374151"
                            }}>
                                ⚡ Faster Response
                            </h4>
                        </div>
                        <p style={{ 
                            margin: "0 0 0 32px",
                            fontSize: "14px", 
                            color: "#6b7280",
                            lineHeight: "1.5"
                        }}>
                            Faster generation with good quality. Best for iteration and most use cases.
                        </p>
                    </div>

                    <div
                        onClick={() => handlePresetChange("accurate")}
                        style={{
                            padding: "16px",
                            borderRadius: "8px",
                            border: preset === "accurate" ? "2px solid #3b82f6" : "2px solid #e5e7eb",
                            backgroundColor: preset === "accurate" ? "#eff6ff" : "#ffffff",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            marginBottom: "8px" 
                        }}>
                            <div style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "50%",
                                border: preset === "accurate" ? "6px solid #3b82f6" : "2px solid #d1d5db",
                                marginRight: "12px",
                                transition: "all 0.2s ease",
                            }} />
                            <h4 style={{ 
                                margin: 0,
                                fontSize: "16px",
                                fontWeight: "600",
                                color: preset === "accurate" ? "#1e40af" : "#374151"
                            }}>
                                🎯 Higher Accuracy
                            </h4>
                        </div>
                        <p style={{ 
                            margin: "0 0 0 32px",
                            fontSize: "14px", 
                            color: "#6b7280",
                            lineHeight: "1.5"
                        }}>
                            Maximum precision with longer processing. Ideal for complex requirements, or high accuracy.
                        </p>
                    </div>
                </div>

                {/* Advanced Toggle */}
                <div style={{
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "16px",
                    marginTop: "8px"
                }}>
                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            background: "none",
                            border: "none",
                            color: "#6b7280",
                            fontSize: "14px",
                            cursor: "pointer",
                            padding: "0",
                            fontWeight: "500",
                            transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "#3b82f6"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "#6b7280"}
                    >
                        <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 16 16" 
                            fill="none"
                            style={{
                                transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease"
                            }}
                        >
                            <path 
                                d="M4 6L8 10L12 6" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                        Advanced Settings
                    </button>

                    {/* Advanced Section */}
                    {showAdvanced && (
                        <div
                            style={{
                                marginTop: "20px",
                            }}
                        >
                            <div style={{
                                backgroundColor: "#f9fafb",
                                padding: "16px",
                                borderRadius: "8px",
                                marginBottom: "16px"
                            }}>
                                <p style={{
                                    margin: 0,
                                    fontSize: "13px",
                                    color: "#6b7280",
                                    lineHeight: "1.5"
                                }}>
                                    ⚠️ Manually selecting models will override the preset configuration. 
                                    Current selection: <strong>{preset === "faster" ? "Faster Response" : "Higher Accuracy"}</strong>
                                </p>
                            </div>
                            
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "20px",
                                }}
                            >
                                <div>
                                    <label
                                        style={{
                                            display: "block",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#374151"
                                        }}
                                    >
                                        Primary Model
                                    </label>
                                    <select
                                        value={model}
                                        onChange={(e) => {
                                            setModel(e.target.value);
                                            setHasChanges(true);
                                            updatePresetFromModels(e.target.value, refinementModel);
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "6px",
                                            fontSize: "14px",
                                            backgroundColor: "white",
                                            transition: "border-color 0.2s ease",
                                            outline: "none",
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                                        onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                                    >
                                        <option value="">Select a model</option>
                                        {modelOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label
                                        style={{
                                            display: "block",
                                            marginBottom: "8px",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#374151"
                                        }}
                                    >
                                        Refinement Model
                                    </label>
                                    <select
                                        value={refinementModel}
                                        onChange={(e) => {
                                            setRefinementModel(e.target.value);
                                            setHasChanges(true);
                                            updatePresetFromModels(model, e.target.value);
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "6px",
                                            fontSize: "14px",
                                            backgroundColor: "white",
                                            transition: "border-color 0.2s ease",
                                            outline: "none",
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                                        onBlur={(e) => e.target.style.borderColor = "#d1d5db"}
                                    >
                                        <option value="">Select a refinement model</option>
                                        {modelOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Passio API Configuration section */}
            {API_KEY_CONFIG_VISIBLE && (
                <div
                    style={{
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        marginBottom: "20px",
                    }}
                >
                    <h3 style={{ marginBottom: "16px" }}>
                        Passio AI Configuration
                    </h3>
                    <div style={{ marginBottom: "16px" }}>
                        <label
                            style={{
                                display: "block",
                                marginBottom: "8px",
                                fontWeight: "500",
                            }}
                        >
                            AI Key
                        </label>
                        {dbUser?.passioapikey ? (
                            <div
                                style={{
                                    padding: "12px",
                                    backgroundColor: "#f0f9ff",
                                    border: "1px solid #3b82f6",
                                    borderRadius: "6px",
                                    fontFamily: "monospace",
                                    fontSize: "14px",
                                    wordBreak: "break-all",
                                }}
                            >
                                {dbUser.passioapikey}
                            </div>
                        ) : (
                            <div
                                style={{
                                    padding: "12px",
                                    backgroundColor: "#fef3c7",
                                    border: "1px solid #f59e0b",
                                    borderRadius: "6px",
                                    color: "#92400e",
                                }}
                            >
                                <strong>No AI key found.</strong> If you have
                                subscribed but don't see your API key, you can
                                manually enter it below. Contact support if you need
                                assistance.
                            </div>
                        )}
                    </div>

                    {!dbUser?.passioapikey && (
                        <div
                            style={{
                                marginTop: "16px",
                                padding: "16px",
                                backgroundColor: "#f9fafb",
                                borderRadius: "6px",
                            }}
                        >
                            <h4
                                style={{
                                    marginBottom: "12px",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                }}
                            >
                                Manual API Key Entry
                            </h4>
                            <p
                                style={{
                                    marginBottom: "12px",
                                    fontSize: "14px",
                                    color: "#6b7280",
                                }}
                            >
                                If automatic provisioning failed, you can manually
                                enter your Passio API key here.
                            </p>
                            <div style={{ display: "flex", gap: "12px" }}>
                                <input
                                    type="text"
                                    placeholder="Enter your Passio AI key"
                                    value={manualApiKey}
                                    onChange={(e) =>
                                        setManualApiKey(e.target.value)
                                    }
                                    style={{
                                        flex: 1,
                                        padding: "8px 12px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                    }}
                                />
                                <button
                                    onClick={handleSaveApiKey}
                                    disabled={!manualApiKey.trim() || savingApiKey}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor:
                                            manualApiKey.trim() && !savingApiKey
                                                ? "#3b82f6"
                                                : "#9ca3af",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor:
                                            manualApiKey.trim() && !savingApiKey
                                                ? "pointer"
                                                : "not-allowed",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {savingApiKey ? "Saving..." : "Save API Key"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {DATA_SOURCE_CONFIG_VISIBLE && (
                <div
                    style={{
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                        }}
                    >
                        <h3 style={{ margin: 0 }}>Data Sources</h3>
                        <div
                            style={{
                                background:
                                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                                padding: "8px 16px",
                                borderRadius: "8px",
                                fontWeight: "600",
                                fontSize: "14px",
                            }}
                        >
                            Total Multiplier:{" "}
                            {calculateTotalMultiplier().toFixed(3)}x
                        </div>
                    </div>
                    {dataSources.map((source, index) => (
                        <div
                            key={source.name}
                            style={{
                                padding: "16px",
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                marginBottom: "12px",
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "12px",
                                }}
                            >
                                <div>
                                    <h4
                                        style={{
                                            margin: "0 0 4px 0",
                                            fontSize: "16px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {formatDataSourceName(source.name)}
                                    </h4>
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            color: "#6b7280",
                                        }}
                                    >
                                        Included: {source.includedResultCount} •
                                        Max: {source.maximumResultCount} •
                                        Multiplier: {source.multiplierPerAdditional}
                                        x
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "14px",
                                            color: "#374151",
                                        }}
                                    >
                                        {source.requestResultCount > 0
                                            ? "Enabled"
                                            : "Disabled"}
                                    </span>
                                    <ToggleSwitch
                                        checked={source.requestResultCount > 0}
                                        onChange={(checked) => {
                                            const newSources = [...dataSources];
                                            newSources[index].requestResultCount =
                                                checked
                                                    ? Math.max(
                                                          1,
                                                          source.includedResultCount
                                                      )
                                                    : 0;
                                            setDataSources(newSources);
                                            setHasChanges(true);
                                        }}
                                    />
                                </div>
                            </div>

                            {source.requestResultCount > 0 && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                        paddingTop: "12px",
                                        borderTop: "1px solid #e5e7eb",
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <label
                                            style={{
                                                display: "block",
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Request Count
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max={source.maximumResultCount}
                                            value={source.requestResultCount}
                                            onChange={(e) => {
                                                const newSources = [...dataSources];
                                                const value = Math.min(
                                                    parseInt(e.target.value) || 0,
                                                    source.maximumResultCount
                                                );
                                                newSources[
                                                    index
                                                ].requestResultCount = value;
                                                setDataSources(newSources);
                                                setHasChanges(true);
                                            }}
                                            style={{
                                                width: "80px",
                                                padding: "6px 8px",
                                                border: "1px solid #d1d5db",
                                                borderRadius: "4px",
                                                fontSize: "14px",
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            Cost Impact
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "14px",
                                                color: "#6b7280",
                                            }}
                                        >
                                            {source.requestResultCount <=
                                            source.includedResultCount
                                                ? "Included in plan"
                                                : `+${(
                                                      (source.requestResultCount -
                                                          source.includedResultCount) *
                                                      source.multiplierPerAdditional
                                                  ).toFixed(3)}x base cost`}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {hasChanges && (
                <div style={{ 
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(8px)",
                    padding: "16px 0",
                    marginTop: "20px",
                    borderTop: "1px solid #e5e7eb"
                }}>
                    <div style={{ 
                        display: "flex", 
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "16px"
                    }}>
                        <span style={{
                            fontSize: "14px",
                            color: "#6b7280"
                        }}>
                            You have unsaved changes
                        </span>
                        <button
                            onClick={handleSave}
                            style={{
                                backgroundColor: "#3b82f6",
                                color: "white",
                                padding: "10px 20px",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600",
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#2563eb";
                                e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#3b82f6";
                                e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
                            }}
                        >
                            Save Configuration
                        </button>
                    </div>
                </div>
            )}


        </div>
    );
};

export default ApiConfigTab;
